'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import { track } from '@/lib/telemetry';
import { CircleNotchIcon } from '@phosphor-icons/react';

import { Card } from '@/components/ui/card';
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { surface } from '@/components/ui/surface';
import { TransactionDetailDrawer } from '@/components/transactions/screen/TransactionDetailDrawer';
import { TransactionRow } from '@/components/transactions/screen/TransactionRow';
import { adaptTransactionForScreen } from '@/components/transactions/screen/TransactionsScreen';
import {
  type CategoryIndex,
  type CategoryMeta,
  UNCATEGORIZED_META,
} from '@/components/transactions/screen/categoryIndex';
import { hashToCategoryColor } from '@/lib/transactions/categories';
import { recurringPaymentsApi, type RecurringPayment } from '@/lib/api';
import { useTransactionRename } from '@/hooks/useTransactionRename';
import { appendPage, type PageRequest, pageItself, rereadLoadedPages } from '@/lib/paging';
import { useRereadOnSpaceWrite } from '@/lib/spaces/sendingChanges';
import type { Transaction } from '@/types';

const HISTORY_PAGE_SIZE = 50;

/**
 * The recurring-payment history drawer — the Recorrentes page's item detail
 * (linked charges + per-transaction drill-down), extracted so other surfaces
 * (the home Despesas Fixas widget) open the exact same experience. Fetches its
 * own history; callers only hand it the full `RecurringPayment` (null closes).
 */
export function HistoryDrawer({
  item,
  onClose,
}: {
  item: RecurringPayment | null;
  onClose: () => void;
}) {
  const { getToken } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  // A space write changes what a charge says it was sent to (`spaceExpenses`, VER-571), often on many of a series'
  // charges at once (a send to every charge with the same description): the loaded history is read again, in place,
  // on the series still open, unless a newer reread or another write to the history landed meanwhile.
  const openItemId = useRef(item?.id);
  useEffect(() => {
    openItemId.current = item?.id;
  });
  // A reread that landed brought the series' history: the first read still on its way shows no spinner over it, and
  // its failure, if it fails afterwards, is no failure to show.
  const rereadLanded = useRef(false);
  const { wrote: wroteHistory, reading: readingHistory } = useRereadOnSpaceWrite((landed) => {
    if (!item) return;
    const itemId = item.id;
    const readPage = async (page: PageRequest) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return recurringPaymentsApi.getTransactions(token, itemId, page);
    };
    rereadLoadedPages(readPage, pageItself, appendPage, transactions.length, HISTORY_PAGE_SIZE)
      .then((page) => {
        if (openItemId.current !== itemId || !landed()) return;
        rereadLanded.current = true;
        setLoading(false);
        setError(false);
        setTransactions(page.items);
        setTotalTransactions(page.total);
        setNextOffset(page.nextOffset);
        setHasMore(page.hasMore);
      })
      .catch((err) => console.error('Recurring history reload failed', err));
  });
  // Rename from the recurring-history drill-down (VER-377). The list is hand-paged
  // local state — no SWR key reaches it — so the single-rename result patches the
  // row in place (the derived selectedTransaction heals with it).
  const rename = useTransactionRename({
    surface: 'recurring_history',
    reading: readingHistory,
    onUpdated: (updated) => {
      setTransactions((current) => current.map((t) => (t.id === updated.id ? updated : t)));
    },
    // A recurring item's history is often exactly the same-description set, so a
    // batch touches many visible rows — patch them all with the name they now carry.
    onBatchApplied: (updatedIds, name) => {
      const ids = new Set(updatedIds);
      setTransactions((current) => current.map(
        (t) => (ids.has(t.id) ? { ...t, customName: name } : t),
      ));
    },
  });

  const screenTransactions = useMemo(
    () => transactions.map(adaptTransactionForScreen),
    [transactions],
  );

  const categoryIndex: CategoryIndex = useMemo(() => {
    const idx = new Map<string, CategoryMeta>();
    for (const tx of screenTransactions) {
      if (idx.has(tx.categoryId)) continue;
      const source = tx.source;
      idx.set(tx.categoryId, {
        id: tx.categoryId,
        label: source.categoryTranslated ?? source.category ?? source.parentCategoryTranslated ?? source.parentCategory ?? 'Outros',
        iconName: source.categoryIcon ?? source.parentCategoryIcon ?? 'circle-dashed',
        color: hashToCategoryColor(tx.categoryId),
      });
    }
    idx.set(UNCATEGORIZED_META.id, UNCATEGORIZED_META);
    return idx;
  }, [screenTransactions]);

  const selectedTransaction = selectedTransactionId
    ? screenTransactions.find((tx) => tx.id === selectedTransactionId) ?? null
    : null;

  const closeTransactionDetails = () => setSelectedTransactionId(null);

  useEffect(() => {
    let cancelled = false;
    wroteHistory();
    if (!item) {
      setTransactions([]);
      setTotalTransactions(0);
      setNextOffset(null);
      setHasMore(false);
      setError(false);
      setExpandedRowId(null);
      setSelectedTransactionId(null);
      return;
    }

    setLoading(true);
    setError(false);
    rereadLanded.current = false;
    const settle = readingHistory();
    getToken()
      .then((token) => {
        if (!token) throw new Error('Not authenticated');
        return recurringPaymentsApi.getTransactions(token, item.id, { limit: HISTORY_PAGE_SIZE, offset: 0 });
      })
      .then((page) => {
        if (!cancelled) {
          settle();
          setTransactions(page.items);
          setTotalTransactions(page.total);
          setNextOffset(page.nextOffset);
          setHasMore(page.hasMore);
        }
      })
      .catch(() => {
        if (!cancelled && !rereadLanded.current) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [getToken, item, readingHistory, wroteHistory]);

  const loadMore = async () => {
    if (!item || !hasMore || nextOffset === null) return;
    setLoadingMore(true);
    const settle = readingHistory();
    try {
      const token = await getToken();
      if (!token) return;
      const page = await recurringPaymentsApi.getTransactions(token, item.id, { limit: HISTORY_PAGE_SIZE, offset: nextOffset });
      settle();
      setTransactions((current) => [...current, ...page.items]);
      setTotalTransactions(page.total);
      setNextOffset(page.nextOffset);
      setHasMore(page.hasMore);
    } catch {
      notifyError('recurring_history', null, 'Não foi possível carregar mais histórico');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <ResponsiveDrawer open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent
        className="flex size-full min-h-0 flex-col overflow-hidden px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="border-b border-border px-5 py-4 text-left">
          <DrawerTitle className="text-lg font-bold">{item?.merchantName ?? 'Recorrente'}</DrawerTitle>
          <DrawerDescription>
            {transactions.length}{totalTransactions > transactions.length ? ` de ${totalTransactions}` : ''} cobranças vinculadas
          </DrawerDescription>
        </DrawerHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-text-tertiary">
              <CircleNotchIcon size={20} className="animate-spin" />
            </div>
          )}
          {error && (
            <Card className="border-error-border bg-error-surface p-4 text-sm font-medium text-error-text">
              Não foi possível carregar o histórico.
            </Card>
          )}
          {!loading && !error && transactions.length === 0 && (
            <Card className="border-dashed p-6 text-center text-sm text-text-secondary">
              Nenhuma transação vinculada ainda.
            </Card>
          )}
          {!loading && !error && screenTransactions.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-none">
              <div className="flex items-baseline justify-between border-b border-border px-5 py-3">
                <p className={surface.label}>Lancamentos</p>
                <p className="text-2xs text-text-secondary tabular-nums">{screenTransactions.length}</p>
              </div>
              <ul className="divide-y divide-border">
                {screenTransactions.map((tx) => (
                  <li key={tx.id}>
                    <TransactionRow
                      tx={tx}
                      expanded={expandedRowId === tx.id}
                      onToggle={() => setExpandedRowId((cur) => (cur === tx.id ? null : tx.id))}
                      onOpenDetails={(id) => {
                        track('action.transaction.open_detail', { source: 'recurring_history' });
                        setSelectedTransactionId(id);
                      }}
                      categoryIndex={categoryIndex}
                    />
                  </li>
                ))}
              </ul>
              {hasMore && (
                <div className="border-t border-border p-3">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="flex h-10 w-full items-center justify-center rounded-xl bg-surface-sunken text-sm font-medium text-text-primary hover:bg-accent disabled:opacity-60"
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <TransactionDetailDrawer
          open={!!selectedTransaction}
          onOpenChange={(open) => {
            if (!open) setSelectedTransactionId(null);
          }}
          onBack={closeTransactionDetails}
          tx={selectedTransaction}
          categoryIndex={categoryIndex}
          onRename={rename.handleRename}
          onPreviewRenameBatch={rename.handlePreviewRenameBatch}
          isRenameSaving={rename.isRenameSaving}
        />
      </DrawerContent>
    </ResponsiveDrawer>
  );
}
